import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axios';

const JobContext = createContext();

export function useJobs() {
  return useContext(JobContext);
}

export function JobProvider({ children }) {
  const [activeJobs, setActiveJobs] = useState([]);
  const [polling, setPolling] = useState(true);

  const fetchActiveJobs = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/jobs/active');
      setActiveJobs(response.data.jobs || []);
    } catch (err) {
      // Silently fail — jobs are not critical for app function
    }
  }, []);

  // Poll every 20 seconds for active jobs (only when authenticated)
  useEffect(() => {
    if (!polling) return;
    const hasAuth = !!localStorage.getItem('auth_user');
    if (!hasAuth) return;
    fetchActiveJobs();
    const interval = setInterval(fetchActiveJobs, 20000);
    return () => clearInterval(interval);
  }, [polling, fetchActiveJobs]);

  const createJob = useCallback(async (controlId, operationType) => {
    const response = await axiosInstance.post('/api/jobs', {
      control_id: controlId,
      operation_type: operationType,
    });
    fetchActiveJobs();
    return response.data;
  }, [fetchActiveJobs]);

  const cancelJob = useCallback(async (jobId) => {
    await axiosInstance.post(`/api/jobs/${jobId}/cancel`);
    fetchActiveJobs();
  }, [fetchActiveJobs]);

  const getJobsForControl = useCallback((controlId) => {
    return activeJobs.filter(j => j.control_id === controlId);
  }, [activeJobs]);

  const hasActiveJob = useCallback((controlId) => {
    return activeJobs.some(j => j.control_id === controlId);
  }, [activeJobs]);

  return (
    <JobContext.Provider value={{
      activeJobs,
      createJob,
      cancelJob,
      getJobsForControl,
      hasActiveJob,
      setPolling,
    }}>
      {children}
    </JobContext.Provider>
  );
}

export default JobContext;
